import { hexToBytes, bytesToHex } from '@noble/hashes/utils'
import { sha256 } from '@noble/hashes/sha2'

/**
 * Validates that a date falls within a time range
 *
 * @param {string|Date} date - Date to check
 * @param {Date} start - Start of valid range
 * @param {Date} end - End of valid range
 * @throws {Error} If date is outside the range
 */
export const checkTimeRange = (date, start, end) => {
  const d = new Date(date)

  if (d.getTime() < start.getTime() || d.getTime() > end.getTime()) {
    throw new Error(`date ${date} is out of range [${start} - ${end}]`)
  }
}

/**
 * Gets a time range starting from now
 *
 * @param {number} maxAgeInSeconds - Duration of the range in seconds
 * @returns {[Date, Date]} Tuple of [now, end]
 */
export const getTimeRangeFromNow = (maxAgeInSeconds) => {
  const now = new Date()
  const end = new Date(now.getTime() + maxAgeInSeconds * 1000)

  return [now, end]
}

/**
 * Sleeps for a specified duration
 *
 * @param {number} milliseconds - Duration to sleep in milliseconds
 * @returns {Promise<void>} Resolves after the duration
 */
export const sleep = (milliseconds) => {
  return new Promise((resolve) => {
    setTimeout(resolve, milliseconds)
  })
}

/**
 * Flattens a nested sandbox object into a flat key-value map
 *
 * Example:
 *   { user: { get: fn1, update: fn2 } }
 *   => { 'user.get': fn1, 'user.update': fn2 }
 *
 * @param {Object} sandbox - Nested object to flatten
 * @param {Object} options - Options
 * @param {string} options.connector - Path separator (default: '.')
 * @returns {Object<string, Function>} Flattened sandbox
 */
export const flattenSandbox = (sandbox = {}, { connector = '.' } = {}) => {
  const result = {}

  const flatten = (path, value) => {
    if (typeof value === 'function') {
      result[path] = value
      return
    }

    if (value && typeof value === 'object') {
      for (const key in value) {
        const nextPath = path === '' ? key : path + connector + key
        flatten(nextPath, value[key])
      }
    }
  }

  flatten('', sandbox)

  return result
}

/**
 * Converts a number or hex string to a 32-byte Uint8Array
 *
 * Used for normalizing Ed25519 private keys.
 *
 * @param {number|string|Uint8Array} input - Input to convert
 * @returns {Uint8Array} 32-byte array
 */
export const to32Bytes = (input) => {
  // Already Uint8Array
  if (input instanceof Uint8Array) {
    if (input.length !== 32) {
      throw new Error(`expected 32 bytes, got ${input.length}`)
    }
    return input
  }

  // Convert to hex string
  let hex = typeof input === 'string' ? input : input.toString(16)

  // Pad to 64 hex characters (32 bytes)
  hex = hex.padStart(64, '0')

  return hexToBytes(hex)
}

/**
 * Wraps all methods in a sandbox with a wrapper function
 *
 * @param {Object<string, Function>} sandbox - Sandbox methods
 * @param {Function} wrapper - Wrapper function (name, fn) => wrappedFn
 * @returns {Object<string, Function>} Wrapped sandbox
 */
export const wrapSandbox = (sandbox, wrapper) => {
  const result = {}

  for (const name in sandbox) {
    result[name] = wrapper(name, sandbox[name])
  }

  return result
}

/**
 * Applies multiple wrappers to a sandbox
 *
 * Wrappers are applied right-to-left (last wrapper is innermost).
 *
 * @param {Object<string, Function>} sandbox - Sandbox methods
 * @param {Function[]} wrappers - Array of wrapper functions
 * @returns {Object<string, Function>} Wrapped sandbox
 */
export const useSandboxWrappers = (sandbox, wrappers) => {
  return wrapSandbox(sandbox, (name, fn) => {
    let wrapped = fn

    // Apply wrappers in reverse order (right-to-left)
    for (let i = wrappers.length - 1; i >= 0; i--) {
      wrapped = wrappers[i](name, wrapped)
    }

    return wrapped
  })
}

/**
 * Logs a message with ISO 8601 timestamp
 *
 * @param {...*} args - Arguments to log
 */
export const log = (...args) => {
  console.log(new Date().toISOString(), ...args)
}

/**
 * Text encoder for converting strings to Uint8Array
 * @type {TextEncoder}
 */
export const textEncoder = new TextEncoder('utf-8')

/**
 * Text decoder for converting Uint8Array to strings
 * @type {TextDecoder}
 */
export const textDecoder = new TextDecoder('utf-8')

export { hexToBytes, bytesToHex, sha256 }
